import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Save, Clock, Info, CheckCircle2, XCircle, Truck } from 'lucide-react';
import AvailabilityScheduler from '../AvailabilityScheduler';

const VendorHyperlocalCard = () => {
  const { apiCall } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await apiCall('/api/shipping/hyperlocal/vendor/settings');
      setSettings(data);
      if (data?.availability?.business_hours?.length) setShowScheduler(true);
    } catch {
      toast.error('Failed to load hyperlocal settings');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // iter-298Q — Tier rates + max_radius_km removed from vendor input.
      // Rates are Shiprocket-Quick-driven; radius is a 50 km carrier fact.
      await apiCall('/api/shipping/hyperlocal/vendor/settings', {
        method: 'PUT',
        body: JSON.stringify({
          is_enabled: settings.is_enabled,
          default_prep_minutes: settings.default_prep_minutes || 15,
          availability: settings.availability || null,
        }),
      });
      toast.success('Hyperlocal settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const cs = settings?.carrier_serviceability || {};

  return (
    <Card data-testid="vendor-hyperlocal-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Hyperlocal Delivery
            </CardTitle>
            <CardDescription>
              Enable Same-Day delivery for eligible listings. Rates come directly from Shiprocket Quick (live) and are automatically discounted by your 5% Shipping Pool contribution — you don&apos;t set slabs.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2" data-testid="hyperlocal-enable-toggle">
            <Label htmlFor="hl-toggle" className="text-sm">Enable</Label>
            <Switch
              id="hl-toggle"
              checked={settings?.is_enabled || false}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, is_enabled: checked }))}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* iter-298Q — Carrier-defined facts strip (READ-ONLY).
            Vendor cannot edit the serviceable radius or set rate slabs — both
            are Shiprocket-controlled. This strip communicates the operating
            envelope + city availability transparently. */}
        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3 space-y-1.5" data-testid="carrier-serviceability-strip">
          <div className="flex items-center gap-2 text-xs">
            <Truck className="h-3.5 w-3.5 text-blue-700" />
            <span className="font-medium text-blue-900">Shiprocket Quick</span>
            <span className="text-blue-800/70">
              — serviceable up to <strong>{cs.max_radius_km || 50} km</strong> (carrier-defined, not editable)
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs" data-testid="city-eligibility-row">
            {cs.available_in_vendor_city ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-700" />
                <span className="text-green-900">
                  Same-Day available in <strong>{cs.vendor_city || 'your city'}</strong>
                </span>
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5 text-amber-700" />
                <span className="text-amber-900">
                  {cs.message || 'Same-Day not yet available in your city'}
                </span>
              </>
            )}
          </div>
          <div className="text-[11px] text-blue-800/70 pl-5">
            Rate: live Shiprocket Quick fare · minus your 5% Shipping Pool subsidy applied at checkout.
          </div>
        </div>

        <div className={`space-y-5 ${!settings?.is_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Prep time */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Default Prep Time (minutes)
              <span
                title="How many minutes your store typically needs to pack an order before a rider arrives. Vendor can override per-order. Default: 15 min."
                className="cursor-help text-gray-400 hover:text-gray-600"
              >
                <Info className="h-3.5 w-3.5" />
              </span>
            </Label>
            <div className="flex items-center gap-2">
              {[10, 15, 20, 30, 45].map((mins) => (
                <button
                  key={mins}
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, default_prep_minutes: mins }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    (settings?.default_prep_minutes || 15) === mins
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'
                  }`}
                  data-testid={`prep-time-${mins}`}
                >
                  {mins} min
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Rider is dispatched {settings?.default_prep_minutes || 15} – 5 = {Math.max((settings?.default_prep_minutes || 15) - 5, 1)} min after order acceptance.
            </p>
          </div>

          {/* Working hours + holidays (AvailabilityScheduler reuse) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Order Window & Holidays</Label>
              <button
                type="button"
                onClick={() => setShowScheduler((v) => !v)}
                className="text-xs text-blue-600 hover:underline"
                data-testid="toggle-scheduler-btn"
              >
                {showScheduler ? 'Hide' : 'Set working hours & holidays'}
              </button>
            </div>
            {showScheduler && (
              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-3">
                  Same-Day rates are only shown at checkout during these hours. Outside the window, customers see
                  the next available slot message instead.
                </p>
                <AvailabilityScheduler
                  availability={settings?.availability || { business_hours: [], holiday_dates: [] }}
                  onChange={(availability) => setSettings(prev => ({ ...prev, availability }))}
                />
              </div>
            )}
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="gap-1.5"
            data-testid="save-hyperlocal-btn"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save Hyperlocal Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VendorHyperlocalCard;
